import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { fillFormBirthDateFromPin } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { App as AntApp, Col, DatePicker, Form, Input, Row } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSearchAgent } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import CommercialRegisterDistrictOfficeAutoComplete from "../../../../enumerations/components/form/CommercialRegisterDistrictOfficeAutoComplete";
import CommercialRegisterSectionAutoComplete from "../../../../enumerations/components/form/CommercialRegisterSectionAutoComplete";
import { AgentType, SecondaryAgentType } from "../../../enums";
import { CreateUpdateLegalSecondaryAgent, CreateUpdateSecondaryAgent } from "../../../types";
import { convertPartialAgentToPartialCreateUpdateSecondaryAgent } from "../../../utils";

const colSpan = 4;

type Props = {
  allowSearch?: boolean;
};

export const LegalSecondaryAgentDataFormPart = ({ allowSearch }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const agentSearch = useSearchAgent();

  const form = Form.useFormInstance<CreateUpdateLegalSecondaryAgent>();

  const handleSearchOrChange = () => {
    form.validateFields(["companyRegistrationNumber"]).then(async (values) => {
      fillFormBirthDateFromPin(values.companyRegistrationNumber, form);
      const searchResult = await agentSearch.mutateAsync({
        keyword: values.companyRegistrationNumber,
        agentType: AgentType.LEGAL,
      });

      if (searchResult.data) {
        message.success(t("agent.helpers.agentFound"));

        const formData = convertPartialAgentToPartialCreateUpdateSecondaryAgent(
          searchResult.data,
        ) as Partial<CreateUpdateSecondaryAgent> & { type: SecondaryAgentType.LEGAL };

        form.setFieldsValue(formData);
      }
    });
  };

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="companyRegistrationNumber"
            label={t("agent.attrs.companyRegistrationNumber")}
            rules={[validations.notBlank, validations.crn]}
          >
            {allowSearch ? (
              <Input.Search
                loading={agentSearch.isPending}
                onSearch={handleSearchOrChange}
                onChange={handleSearchOrChange}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="companyName"
            label={t("agent.attrs.companyName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.agentSortNameRegex),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <CommercialRegisterDistrictOfficeAutoComplete
            formItemProps={{
              name: "commercialRegisterDistrictOffice",
              label: t("agent.attrs.commercialRegisterDistrictOffice"),
              rules: [validations.size(1, 64)],
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="taxId" label={t("agent.attrs.taxId")} rules={[validations.length(10), validations.numeric]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <CommercialRegisterSectionAutoComplete
            formItemProps={{
              name: "commercialRegisterSection",
              label: t("agent.attrs.commercialRegisterSection"),
              rules: [validations.size(1, 64)],
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="vatId"
            label={<LabelWithTooltip label={t("agent.attrs.vatId")} tooltip={t("agent.helpers.vatPayerHint")} />}
            rules={[validations.length(12), validations.pattern(regexPatterns.vatIdRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="commercialRegisterInsertionNumber"
            label={t("agent.attrs.commercialRegisterInsertionNumber")}
            rules={[validations.size(1, 64)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={colSpan}>
          <Form.Item
            name="legalTypeChangeDate"
            label={t("agent.attrs.legalTypeChangeDate")}
            rules={[validations.notFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} maxDate={dayjs()} allowClear={true} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
